import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { CalendarBlank, DownloadSimple, Clock, CurrencyEur, Printer, EnvelopeSimple, FileXls, Tag, FileText } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { openDownload } from "@/lib/downloadUrl";
import { useAuth, usePermissions } from "@/App";
import EtikettenDialog from "@/components/EtikettenDialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLoonZichtbaar } from "@/components/LoonSlot";

export const KLEUREN = ["#C62828", "#EF6C00", "#F9A825", "#2E7D32", "#00838F", "#002FA7", "#6A1B9A", "#AD1457", "#424242"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAAND_NAMEN = [
  "Januari", "Februari", "Maart", "April", "Mei", "Juni",
  "Juli", "Augustus", "September", "Oktober", "November", "December"
];

export default function Maandoverzicht() {
  const user = useAuth();
  const perms = usePermissions();
  const loonZichtbaar = useLoonZichtbaar();
  const canLonen = perms[user?.role || "medewerker"]?.lonen_zien === true && loonZichtbaar;
  const canKleur = perms[user?.role || "medewerker"]?.medewerkers_bewerken === true;

  const zetKleur = async (medewerkerId, kleur) => {
    try {
      await axios.put(`${API}/medewerkers/${medewerkerId}`, { kleur });
      await fetchData();
    } catch (e) {
      toast.error("Kleur opslaan mislukt");
    }
  };

  const now = new Date();
  const [jaar, setJaar] = useState(String(now.getFullYear()));
  const [maand, setMaand] = useState(String(now.getMonth() + 1));
  const [filiaalId, setFiliaalId] = useState("alles");
  const [data, setData] = useState(null);
  const [verlofMatrix, setVerlofMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [etikettenOpen, setEtikettenOpen] = useState(false);
  useEffect(() => { if (!canLonen) { setEtikettenOpen(false); setData(null); } }, [canLonen]);
  const requestVersion = useRef(0);
  const [emailTo, setEmailTo] = useState("");
  const [sending, setSending] = useState(false);
  const [recentEmails, setRecentEmails] = useState(() => {
    try { return JSON.parse(localStorage.getItem("recent_report_emails") || "[]"); }
    catch { return []; }
  });
  const [zoom, setZoom] = useState(() => {
    const saved = parseInt(localStorage.getItem("maand_zoom") || "80", 10);
    return Number.isFinite(saved) ? saved : 80;
  });
  useEffect(() => { localStorage.setItem("maand_zoom", String(zoom)); }, [zoom]);

  const filiaalParam = filiaalId === "alles" ? "" : `&filiaal_id=${encodeURIComponent(filiaalId)}`;

  const fetchData = useCallback(async () => {
    const current = ++requestVersion.current;
    setLoading(true);
    try {
      const params = { jaar: parseInt(jaar), maand: parseInt(maand) };
      if (filiaalId !== "alles") params.filiaal_id = filiaalId;
      const [res, vRes] = await Promise.all([
        axios.get(`${API}/maandoverzicht`, { params }),
        axios.get(`${API}/verlof/matrix`, { params: { jaar: parseInt(jaar), maand: parseInt(maand) } }).catch(() => ({ data: null })),
      ]);
      if (current !== requestVersion.current) return;
      setData(res.data);
      setVerlofMatrix(vRes.data || null);
    } catch (e) {
      if (current === requestVersion.current) toast.error("Fout bij ophalen maandoverzicht");
    } finally {
      if (current === requestVersion.current) setLoading(false);
    }
  }, [jaar, maand, filiaalId, loonZichtbaar]);

  useEffect(() => {
    fetchData();
    return () => { requestVersion.current++; };
  }, [fetchData]);

  const handleExportCSV = () => {
    openDownload(`${API}/export/maandoverzicht?jaar=${jaar}&maand=${maand}${filiaalParam}`);
  };

  const handleExportExcel = () => {
    openDownload(`${API}/export/maandoverzicht-excel?jaar=${jaar}&maand=${maand}${filiaalParam}`);
  };

  const downloadLoonbrief = (medewerkerId) => {
    openDownload(`${API}/loonbrief/${medewerkerId}/pdf?jaar=${jaar}&maand=${maand}`);
  };

  const downloadAlleLoonbrieven = () => {
    openDownload(`${API}/loonbrief/maand/pdf?jaar=${jaar}&maand=${maand}${filiaalParam}`);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSendEmail = async () => {
    if (!emailTo) { toast.error("Vul een email adres in"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/email/rapport`, {
        email: emailTo, type: "maand", jaar: parseInt(jaar), maand: parseInt(maand),
        filiaal_id: filiaalId === "alles" ? null : filiaalId,
      });
      toast.success(`Rapport verstuurd naar ${emailTo}`);
      // Bewaar adres in recent-lijst (max 5)
      const next = [emailTo, ...recentEmails.filter((e) => e !== emailTo)].slice(0, 5);
      setRecentEmails(next);
      localStorage.setItem("recent_report_emails", JSON.stringify(next));
      setEmailOpen(false);
      setEmailTo("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fout bij versturen");
    } finally { setSending(false); }
  };

  const removeRecent = (addr) => {
    const next = recentEmails.filter((e) => e !== addr);
    setRecentEmails(next);
    localStorage.setItem("recent_report_emails", JSON.stringify(next));
  };

  const formatUren = (val) => {
    if (!val || val === 0) return "";
    return String(val).replace(".", ",");
  };

  const permRol = user?.role === "superadmin" ? "admin" : (user?.role || "medewerker");
  const isAdmin = perms[permRol]?.uren_invullen === true;
  const kanDagInvullen = isAdmin && filiaalId !== "alles";
  const [bewerkModus, setBewerkModus] = useState(false);
  const [alleMedewerkers, setAlleMedewerkers] = useState([]);
  const [extraRijen, setExtraRijen] = useState([]);
  const [dagWijzigingen, setDagWijzigingen] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);

  useEffect(() => {
    setBewerkModus(false);
    setDagWijzigingen({});
    setExtraRijen([]);
  }, [filiaalId, jaar, maand]);

  useEffect(() => {
    if (!isAdmin) return;
    axios.get(`${API}/medewerkers`).then((r) => setAlleMedewerkers(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const voegMedewerkerToe = (medewerkerId) => {
    const m = alleMedewerkers.find((x) => x.id === medewerkerId);
    if (!m) return;
    const dagenAantal = data?.dagen?.length || 31;
    setExtraRijen((prev) => [
      ...prev,
      {
        medewerker_id: m.id,
        naam: `${m.voornaam} ${m.achternaam}`,
        kleur: m.kleur || "",
        uurloon: m.uurloon || 0,
        uren_per_dag: Array(dagenAantal).fill(0),
        totaal_uren: 0,
        uitbetaald_uren: 0,
        verschil_uren: 0,
        totaal_loon: 0,
        filiaal_detail: [],
        is_extra: true,
      },
    ]);
    if (!bewerkModus) setBewerkModus(true);
  };

  const dagDatum = (dagNummer) =>
    `${jaar}-${String(parseInt(maand)).padStart(2, "0")}-${String(dagNummer).padStart(2, "0")}`;

  const zetWijziging = (medewerkerId, dagNummer, waarde) =>
    setDagWijzigingen((prev) => ({ ...prev, [`${medewerkerId}-${dagNummer}`]: waarde }));

  const aantalWijzigingen = Object.keys(dagWijzigingen).length;

  const saveAlleWijzigingen = async () => {
    const items = Object.entries(dagWijzigingen);
    if (items.length === 0) { setBewerkModus(false); return; }
    for (const [, waarde] of items) {
      const u = parseFloat(String(waarde ?? "").replace(",", ".")) || 0;
      if (u < 0 || u > 24) { toast.error("Uren moeten tussen 0 en 24 liggen"); return; }
    }
    setBulkSaving(true);
    try {
      for (const [key, waarde] of items) {
        const idx = key.lastIndexOf("-");
        const medewerkerId = key.slice(0, idx);
        const dagNummer = key.slice(idx + 1);
        await axios.put(`${API}/aanwezigheid/dag-uren`, {
          medewerker_id: medewerkerId,
          filiaal_id: filiaalId,
          datum: dagDatum(dagNummer),
          uren: parseFloat(String(waarde ?? "").replace(",", ".")) || 0,
        });
      }
      setDagWijzigingen({});
      setExtraRijen([]);
      setBewerkModus(false);
      await fetchData();
      toast.success(`${items.length} wijziging${items.length === 1 ? "" : "en"} opgeslagen`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Uren opslaan mislukt");
    } finally { setBulkSaving(false); }
  };

  const formatLoon = (val) => {
    if (!val && val !== 0) return "0,00";
    return val.toFixed(2).replace(".", ",");
  };

  const [uitbEdit, setUitbEdit] = useState({});
  const [openKaart, setOpenKaart] = useState(null);
  const [savingUitb, setSavingUitb] = useState(null);

  const saveUitbetaald = async (medewerkerId, waarde) => {
    const uren = parseFloat(String(waarde).replace(",", ".")) || 0;
    setSavingUitb(medewerkerId);
    try {
      await axios.put(`${API}/uitbetaalde-uren`, {
        medewerker_id: medewerkerId,
        jaar: parseInt(jaar),
        maand: parseInt(maand),
        uren,
      });
      setUitbEdit((prev) => {
        const next = { ...prev };
        delete next[medewerkerId];
        return next;
      });
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSavingUitb(null);
    }
  };

  return (
    <div data-testid="maandoverzicht-page" className="space-y-6">
      {/* Header - hidden in print */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 print:hidden">
        <div>
          <h1 className="page-title">Maandoverzicht</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {canLonen ? "Uren en loonkosten per medewerker per dag" : "Gewerkte uren per medewerker per dag"}
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Select value={maand} onValueChange={setMaand}>
            <SelectTrigger className="rounded-sm w-full sm:w-36" data-testid="select-maand">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MAAND_NAMEN.map((naam, idx) => (
                <SelectItem key={idx} value={String(idx + 1)}>
                  {naam}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={jaar} onValueChange={setJaar}>
            <SelectTrigger className="rounded-sm flex-1 sm:w-24" data-testid="select-jaar">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map((y) => (
                <SelectItem key={y} value={String(y)}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filiaalId} onValueChange={setFiliaalId}>
            <SelectTrigger className="rounded-sm w-full sm:w-44" data-testid="select-filiaal">
              <SelectValue placeholder="Alle filialen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="alles">Alle filialen</SelectItem>
              {(data?.filialen || []).map((f) => (
                <SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>
              ))}
              <SelectItem value="geen">Geen filiaal</SelectItem>
            </SelectContent>
          </Select>
          {canLonen && <Button onClick={handleExportCSV} variant="outline" className="rounded-sm flex-1 sm:flex-none" data-testid="export-csv-btn">
            <DownloadSimple size={18} weight="bold" className="mr-2" />
            CSV
          </Button>}
          {canLonen && <Button onClick={handleExportExcel} variant="outline" className="rounded-sm flex-1 sm:flex-none" data-testid="export-excel-btn">
            <FileXls size={18} weight="bold" className="mr-2" />
            Excel
          </Button>}
          {canLonen && <Button onClick={() => setEmailOpen(true)} variant="outline" className="rounded-sm flex-1 sm:flex-none" data-testid="email-btn">
            <EnvelopeSimple size={18} weight="bold" className="mr-2" />
            Email
          </Button>}
          {canLonen && <Button onClick={() => setEtikettenOpen(true)} variant="outline" className="rounded-sm flex-1 sm:flex-none" data-testid="etiketten-btn">
            <Tag size={18} weight="bold" className="mr-2" />
            Etiketten
          </Button>}
          {canLonen && <Button onClick={downloadAlleLoonbrieven} variant="outline" className="rounded-sm flex-1 sm:flex-none border-[#002FA7] text-[#002FA7]" data-testid="loonbrieven-btn">
            <FileText size={18} weight="bold" className="mr-2" />
            Loonbrieven
          </Button>}
          <Button onClick={handlePrint} variant="outline" className="rounded-sm flex-1 sm:flex-none" data-testid="print-btn">
            <Printer size={18} weight="bold" className="mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Print header - only visible in print */}
      <div className="hidden print:block print:mb-4">
        <h1 className="text-xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Maandoverzicht — {MAAND_NAMEN[parseInt(maand) - 1]} {jaar}
          {filiaalId !== "alles" && ` — ${filiaalId === "geen" ? "Geen filiaal" : (data?.filialen || []).find((f) => f.id === filiaalId)?.naam || ""}`}
        </h1>
      </div>

      {/* Totals - hidden in print */}
      {data && (
        <div className="flex gap-4 flex-wrap print:hidden">
          <div className="stat-card flex items-center gap-3" data-testid="totaal-uren-card">
            <Clock size={20} weight="bold" className="text-[#002FA7]" />
            <div>
              <p className="section-label">Totaal Uren</p>
              <p className="text-xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {formatUren(data.totaal_uren) || "0"}
              </p>
            </div>
          </div>
          {canLonen && (
          <div className="stat-card flex items-center gap-3" data-testid="totaal-loon-card">
            <CurrencyEur size={20} weight="bold" className="text-[#002FA7]" />
            <div>
              <p className="section-label">Totaal Loon</p>
              <p className="text-xl font-black" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {"\u20AC"}{formatLoon(data.totaal_loon)}
              </p>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Totaal per filiaal */}
      {data && (data.per_filiaal || []).length > 0 && (
        <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="per-filiaal-blok">
          <div className="px-4 py-2.5 border-b border-border bg-[#E0FFFF] flex items-center justify-between">
            <h2 className="text-sm font-black uppercase tracking-wider" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Totaal per filiaal
            </h2>
            <span className="text-[11px] font-semibold text-muted-foreground">
              {MAAND_NAMEN[parseInt(maand) - 1]} {jaar}
            </span>
          </div>
          <div className="divide-y divide-border">
            {data.per_filiaal.map((f) => (
              <button
                key={f.filiaal_id}
                type="button"
                onClick={() => setFiliaalId(filiaalId === f.filiaal_id ? "alles" : f.filiaal_id)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-muted/60 transition-colors ${filiaalId === f.filiaal_id ? "bg-[#E0FFFF]/60" : ""}`}
                data-testid={`per-filiaal-rij-${f.filiaal_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate">{f.naam}</p>
                  <p className="text-[11px] text-muted-foreground">{f.medewerkers} medewerker{f.medewerkers === 1 ? "" : "s"}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-black tabular-nums">{formatUren(f.uren) || "0"} u</p>
                  {canLonen && <p className="text-[11px] font-bold text-[#002FA7] tabular-nums">{"\u20AC"}{formatLoon(f.loon)}</p>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Grid Table - matches the example exactly */}
      {loading ? (
        <div className="h-96 bg-muted animate-pulse rounded-sm print:hidden" />
      ) : data ? (
        <>
          {isAdmin && (
            <div className="flex flex-wrap items-center gap-2 print:hidden" data-testid="dagcel-balk">
              {!kanDagInvullen ? (
                <p className="text-[11px] text-muted-foreground" data-testid="dagcel-hint">
                  Kies één filiaal hierboven om de gewerkte uren in het grid te kunnen invullen.
                </p>
              ) : !bewerkModus ? (
                <Button
                  onClick={() => setBewerkModus(true)}
                  size="sm"
                  variant="outline"
                  className="rounded-sm h-8 text-xs"
                  data-testid="uren-bewerken-btn"
                >
                  Uren invullen / aanpassen
                </Button>
              ) : (
                <>
                  <span className="text-[11px] font-semibold text-[#002FA7]" data-testid="dagcel-hint">
                    Vul de uren in het grid in — {aantalWijzigingen} wijziging{aantalWijzigingen === 1 ? "" : "en"} nog niet opgeslagen
                  </span>
                  <Button
                    onClick={saveAlleWijzigingen}
                    disabled={bulkSaving}
                    size="sm"
                    className="rounded-sm h-8 text-xs bg-[#002FA7] hover:bg-[#002280] text-white"
                    data-testid="uren-opslaan-btn"
                  >
                    {bulkSaving ? "Opslaan..." : "Opslaan"}
                  </Button>
                  <Select value="" onValueChange={voegMedewerkerToe}>
                    <SelectTrigger className="rounded-sm h-8 text-xs w-[220px]" data-testid="medewerker-toevoegen-select">
                      <SelectValue placeholder="+ Medewerker toevoegen" />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {alleMedewerkers
                        .filter((m) => ![...data.rijen, ...extraRijen].some((r) => r.medewerker_id === m.id))
                        .map((m) => (
                          <SelectItem key={m.id} value={m.id}>{m.voornaam} {m.achternaam}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => { setDagWijzigingen({}); setExtraRijen([]); setBewerkModus(false); }}
                    disabled={bulkSaving}
                    size="sm"
                    variant="outline"
                    className="rounded-sm h-8 text-xs"
                    data-testid="uren-annuleren-btn"
                  >
                    Annuleren
                  </Button>
                </>
              )}
            </div>
          )}
          {/* Zoom slider - door gebruiker aan te passen */}
          <div className="hidden md:flex items-center justify-end gap-2 mb-2 print:hidden" data-testid="zoom-control">
            <span className="text-[11px] font-semibold text-muted-foreground">Grootte:</span>
            <button type="button" onClick={() => setZoom(Math.max(50, zoom - 10))} className="w-6 h-6 rounded-sm border border-border text-xs font-bold hover:bg-muted" data-testid="zoom-smaller" title="Kleiner">−</button>
            <input type="range" min="50" max="150" step="5" value={zoom} onChange={(e) => setZoom(parseInt(e.target.value, 10))} className="w-32 accent-[#002FA7]" data-testid="zoom-slider" />
            <button type="button" onClick={() => setZoom(Math.min(150, zoom + 10))} className="w-6 h-6 rounded-sm border border-border text-xs font-bold hover:bg-muted" data-testid="zoom-larger" title="Groter">+</button>
            <button type="button" onClick={() => setZoom(80)} className="text-[10px] font-semibold px-2 py-0.5 rounded-sm border border-border hover:bg-muted tabular-nums min-w-[42px]" data-testid="zoom-reset" title="Reset naar 80%">{zoom}%</button>
          </div>
        {/* Mobiele kaartweergave */}
        <div className="mob-list print:hidden" data-testid="maand-mobiel">
          {data.rijen.map((rij) => {
            const open = openKaart === rij.medewerker_id;
            const gewerkteDagen = rij.uren_per_dag
              .map((uren, idx) => ({ uren, dag: data.dagen[idx] }))
              .filter((x) => x.uren > 0);
            return (
              <div key={rij.medewerker_id} className="mob-card" data-testid={`maand-kaart-${rij.medewerker_id}`}>
                <button
                  type="button"
                  onClick={() => setOpenKaart(open ? null : rij.medewerker_id)}
                  className="w-full text-left"
                  data-testid={`maand-kaart-toggle-${rij.medewerker_id}`}
                >
                  <div className="mob-card-head">
                    <p className="mob-title" style={{ color: rij.kleur || undefined }}>{rij.naam}</p>
                    <div className="text-right shrink-0">
                      <span className="mob-value">{formatUren(rij.totaal_uren) || "0"} u</span>
                      {canLonen && <span className="block text-[10px] font-bold text-[#002FA7]">{"\u20AC"}{formatLoon(rij.totaal_loon)}</span>}
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {gewerkteDagen.length} dag{gewerkteDagen.length === 1 ? "" : "en"} gewerkt · tik voor details
                  </p>
                  {rij.filialen && (
                    <p className="text-[10px] font-semibold text-[#002FA7] mt-0.5" data-testid={`mob-filialen-${rij.medewerker_id}`}>
                      {rij.filialen}
                    </p>
                  )}
                </button>

                {canLonen && (
                  <div className="flex items-center justify-between gap-2 mt-2 pt-2 border-t border-border">
                    <label className="mob-label" htmlFor={`mob-uitb-${rij.medewerker_id}`}>Uitbetaald uur</label>
                    <div className="flex items-center gap-2">
                      <input
                        id={`mob-uitb-${rij.medewerker_id}`}
                        type="text"
                        inputMode="decimal"
                        value={uitbEdit[rij.medewerker_id] ?? (rij.uitbetaald_uren ? formatUren(rij.uitbetaald_uren) : "")}
                        onChange={(e) => setUitbEdit({ ...uitbEdit, [rij.medewerker_id]: e.target.value })}
                        onBlur={(e) => { if (uitbEdit[rij.medewerker_id] !== undefined) saveUitbetaald(rij.medewerker_id, e.target.value); }}
                        placeholder="0"
                        className="w-16 h-9 px-2 text-center text-xs border border-border rounded-sm tabular-nums"
                        data-testid={`mob-uitbetaald-input-${rij.medewerker_id}`}
                      />
                      <span className="mob-value" data-testid={`mob-verschil-${rij.medewerker_id}`}>
                        = {formatUren(rij.verschil_uren) || "0"} u
                      </span>
                      <button
                        type="button"
                        onClick={() => downloadLoonbrief(rij.medewerker_id)}
                        className="h-9 px-2 flex items-center gap-1 rounded-sm border border-[#002FA7]/30 text-[11px] font-bold text-[#002FA7]"
                        data-testid={`mob-loonbrief-btn-${rij.medewerker_id}`}
                      >
                        <FileText size={13} weight="bold" />
                        Loonbrief
                      </button>
                    </div>
                  </div>
                )}

                {open && (
                  <div className="mt-2 pt-2 border-t border-border grid grid-cols-2 gap-1" data-testid={`maand-dagen-${rij.medewerker_id}`}>
                    {gewerkteDagen.length === 0 ? (
                      <p className="col-span-2 text-xs text-muted-foreground">Geen gewerkte dagen deze maand</p>
                    ) : gewerkteDagen.map(({ uren, dag }) => (
                      <div key={dag.dag} className={`flex items-center justify-between px-2 py-1 rounded-sm text-xs ${dag.is_feestdag ? "bg-[#8E24AA] text-white" : dag.is_zondag ? "bg-[#FF6666] text-white" : dag.is_zaterdag ? "bg-[#00CED1] text-white" : "bg-[#E0FFFF]"}`}>
                        <span className="font-semibold">
                          {dag.dag} {dag.dag_naam}
                          {dag.is_feestdag && <span className="ml-1 font-bold">· {dag.feestdag_naam}</span>}
                        </span>
                        <span className="tabular-nums font-bold">{formatUren(uren)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div className="mob-card bg-[#E0FFFF]" data-testid="maand-mobiel-totaal">
            <div className="mob-row">
              <span className="text-xs font-black uppercase tracking-wider">Totaal uren</span>
              <span className="text-sm font-black tabular-nums">{formatUren(data.totaal_uren)}</span>
            </div>
            {canLonen && (
              <>
                <div className="mob-row">
                  <span className="mob-label">Uitbetaald / verschil</span>
                  <span className="mob-value">{formatUren(data.totaal_uitbetaald)} / {formatUren(data.totaal_verschil)}</span>
                </div>
                <div className="mob-row">
                  <span className="text-xs font-black uppercase tracking-wider">Bedrag</span>
                  <span className="text-sm font-black tabular-nums">{"\u20AC"}{formatLoon(data.totaal_loon)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="hidden md:block print:block bg-white border border-[#999] rounded-sm overflow-x-auto print:overflow-visible print:rounded-none print:border-black" data-testid="maandoverzicht-table" id="maandoverzicht-print" style={{ zoom: zoom / 100 }}>
          <table className="text-[11px] border-collapse w-full print:text-[9px]" style={{ minWidth: "900px" }}>
            <thead>
              {/* Day numbers row */}
              <tr>
                <th className="sticky left-0 z-10 bg-[#E0FFFF] px-2 py-1.5 text-left font-bold border border-[#999] min-w-[120px] print:static print:min-w-0">
                  Personeel
                </th>
                {data.dagen.map((d) => (
                  <th
                    key={d.dag}
                    title={d.is_feestdag ? `Feestdag: ${d.feestdag_naam}` : ""}
                    className={`px-0.5 py-1 text-center font-bold min-w-[28px] border border-[#999] print:min-w-0 ${
                      d.is_feestdag
                        ? "bg-[#8E24AA] text-white"
                        : d.is_zondag
                        ? "bg-[#FF6666] text-white"
                        : d.is_zaterdag
                        ? "bg-[#00CED1] text-white"
                        : "bg-[#E0FFFF]"
                    }`}
                  >
                    {d.dag}
                  </th>
                ))}
                <th className="px-1 py-1.5 text-center font-bold bg-[#E0FFFF] border border-[#999] min-w-[55px] print:min-w-0 text-[10px]">totaal uur</th>
                {canLonen && <th className="px-1 py-1.5 text-center font-bold bg-[#E0FFFF] border border-[#999] w-[48px] min-w-[48px] print:min-w-0 text-[9px] leading-tight">uitbet. uur</th>}
                {canLonen && <th className="px-1 py-1.5 text-center font-bold bg-[#E0FFFF] border border-[#999] w-[48px] min-w-[48px] print:min-w-0 text-[9px] leading-tight">verschil</th>}
                {canLonen && <th className="px-1 py-1.5 text-center font-bold bg-[#E0FFFF] border border-[#999] min-w-[50px] print:min-w-0">Uurloon</th>}
                {canLonen && <th className="px-1 py-1.5 text-center font-bold bg-[#E0FFFF] border border-[#999] min-w-[55px] print:min-w-0">Bedrag &euro;</th>}
                <th className="px-1 py-1.5 text-left font-bold bg-[#E0FFFF] border border-[#999] min-w-[130px] print:min-w-0 text-[10px]">Filialen</th>
              </tr>
              {/* Day names row */}
              <tr>
                <th className="sticky left-0 z-10 bg-[#E0FFFF] px-2 py-0.5 border border-[#999] print:static"></th>
                {data.dagen.map((d) => (
                  <th
                    key={`name-${d.dag}`}
                    title={d.is_feestdag ? `Feestdag: ${d.feestdag_naam}` : ""}
                    className={`px-0.5 py-0.5 text-center text-[9px] font-medium border border-[#999] print:text-[7px] ${
                      d.is_feestdag
                        ? "bg-[#8E24AA] text-white"
                        : d.is_zondag
                        ? "bg-[#FF6666] text-white"
                        : d.is_zaterdag
                        ? "bg-[#00CED1] text-white"
                        : "bg-[#E0FFFF]"
                    }`}
                  >
                    {d.dag_naam}
                  </th>
                ))}
                <th className="bg-[#E0FFFF] border border-[#999]"></th>
                {canLonen && <th className="bg-[#E0FFFF] border border-[#999]"></th>}
                {canLonen && <th className="bg-[#E0FFFF] border border-[#999]"></th>}
                {canLonen && <th className="bg-[#E0FFFF] border border-[#999]"></th>}
                {canLonen && <th className="bg-[#E0FFFF] border border-[#999]"></th>}
                <th className="bg-[#E0FFFF] border border-[#999]"></th>
              </tr>
            </thead>
            <tbody>
              {[...data.rijen, ...extraRijen.filter((e) => !data.rijen.some((r) => r.medewerker_id === e.medewerker_id))].map((rij) => (
                <tr key={rij.medewerker_id} data-testid={`overzicht-row-${rij.medewerker_id}`}>
                  <td className="sticky left-0 z-10 bg-white px-2 py-1 font-medium text-[11px] border border-[#999] whitespace-nowrap print:static print:text-[9px]">
                    {canKleur ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="hover:underline decoration-dotted"
                            style={{ color: rij.kleur || undefined }}
                            title="Klik om kleur te kiezen"
                            data-testid={`naam-kleur-btn-${rij.medewerker_id}`}
                          >
                            {rij.naam}
                          </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-2 rounded-sm" align="start">
                          <div className="flex items-center gap-1.5">
                            {KLEUREN.map((k) => (
                              <button
                                key={k}
                                type="button"
                                onClick={() => zetKleur(rij.medewerker_id, k)}
                                className="w-6 h-6 rounded-sm border border-border hover:scale-110 transition-transform"
                                style={{ backgroundColor: k }}
                                data-testid={`kleur-${k.replace("#", "")}-${rij.medewerker_id}`}
                              />
                            ))}
                            <button
                              type="button"
                              onClick={() => zetKleur(rij.medewerker_id, "")}
                              className="h-6 px-2 text-[10px] font-semibold border border-border rounded-sm hover:bg-muted"
                              data-testid={`kleur-wis-${rij.medewerker_id}`}
                            >
                              Geen
                            </button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <span style={{ color: rij.kleur || undefined }}>{rij.naam}</span>
                    )}
                    {canLonen && (
                      <button
                        type="button"
                        onClick={() => downloadLoonbrief(rij.medewerker_id)}
                        title={`Loonbrief ${rij.naam} downloaden`}
                        className="ml-1.5 text-[#002FA7] align-middle print:hidden"
                        data-testid={`loonbrief-btn-${rij.medewerker_id}`}
                      >
                        <FileText size={13} weight="bold" />
                      </button>
                    )}
                  </td>
                  {rij.uren_per_dag.map((uren, idx) => {
                    const dag = data.dagen[idx];
                    // Vind verlof voor deze medewerker op deze dag
                    let verlofCell = null;
                    if (verlofMatrix) {
                      const vRow = verlofMatrix.rijen.find((r) => r.medewerker_id === rij.medewerker_id);
                      if (vRow) verlofCell = vRow.dagen[idx];
                    }
                    const VERLOF_LETTER = { vakantie: "V", ziek: "Z", feestdag: "F", onbetaald: "O", compensatie: "C", anders: "A" };
                    const VERLOF_BG = {
                      vakantie: "#DBEAFE", ziek: "#FEE2E2", feestdag: "#FFEDD5",
                      onbetaald: "#FEF3C7", compensatie: "#D1FAE5", anders: "#F3F4F6",
                    };
                    let cellBg = "bg-white";
                    let cellStyle = undefined;
                    let cellContent = formatUren(uren);
                    let extraCls = uren > 0 ? "font-semibold text-black" : "";
                    if (dag.is_feestdag) {
                      cellBg = "bg-[#8E24AA]";
                      if (uren > 0) extraCls = "font-semibold text-white";
                    } else if (dag.is_zondag) {
                      cellBg = "bg-[#FF6666]";
                      if (uren > 0) extraCls = "font-semibold text-white";
                    } else if (dag.is_zaterdag) {
                      cellBg = "bg-[#00CED1]";
                      if (uren > 0) extraCls = "font-semibold text-black";
                    } else if (uren > 0) {
                      cellBg = "bg-[#E0FFFF]";
                    }
                    // Verlof overrules op werkdagen (en op weekenden waar geen uren staan)
                    if (verlofCell && uren === 0 && !dag.is_zondag && !dag.is_feestdag) {
                      cellBg = "";
                      cellStyle = { backgroundColor: VERLOF_BG[verlofCell.type] || "#F3F4F6", color: "#1F2937" };
                      cellContent = verlofCell.halve_dag ? "½" : (VERLOF_LETTER[verlofCell.type] || "A");
                      extraCls = "font-bold";
                    }
                    const dagNr = dag.dag ?? dag.dag_num ?? idx + 1;
                    const cellKey = `${rij.medewerker_id}-${dagNr}`;
                    const inEdit = kanDagInvullen && bewerkModus;
                    const editWaarde = dagWijzigingen[cellKey] !== undefined
                      ? dagWijzigingen[cellKey]
                      : (uren ? String(uren).replace(".", ",") : "");
                    const gewijzigd = dagWijzigingen[cellKey] !== undefined;
                    return (
                      <td
                        key={idx}
                        className={`relative px-0.5 py-1 text-center tabular-nums border border-[#999] ${cellBg} ${extraCls}`}
                        style={cellStyle}
                        title={dag.is_feestdag ? `Feestdag: ${dag.feestdag_naam}` : (verlofCell ? verlofCell.type : "")}
                        data-testid={kanDagInvullen ? `dagcel-${cellKey}` : undefined}
                      >
                        {inEdit ? (
                          <>
                            <span className="invisible">{cellContent || "0"}</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={editWaarde}
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => zetWijziging(rij.medewerker_id, dagNr, e.target.value)}
                              className={`absolute inset-0 w-full h-full px-0 text-center outline-none tabular-nums font-semibold text-black bg-transparent ${gewijzigd ? "bg-[#FFF3C4] ring-1 ring-[#F9A825]" : ""} focus:bg-white focus:ring-2 focus:ring-[#002FA7]`}
                              data-testid={`dagcel-input-${cellKey}`}
                            />
                          </>
                        ) : (
                          cellContent
                        )}
                      </td>
                    );
                  })}
                  <td className="px-1 py-1 text-center font-bold border border-[#999] tabular-nums bg-white">
                    {formatUren(rij.totaal_uren) || "0"}
                  </td>
                  {canLonen && (
                  <td className="p-0 text-center border border-[#999] tabular-nums bg-white w-[48px]">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={uitbEdit[rij.medewerker_id] ?? (rij.uitbetaald_uren ? formatUren(rij.uitbetaald_uren) : "")}
                      onChange={(e) => setUitbEdit({ ...uitbEdit, [rij.medewerker_id]: e.target.value })}
                      onBlur={(e) => {
                        if (uitbEdit[rij.medewerker_id] !== undefined) saveUitbetaald(rij.medewerker_id, e.target.value);
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      disabled={savingUitb === rij.medewerker_id}
                      placeholder="0"
                      className="w-full h-full px-1 py-1 text-center tabular-nums bg-transparent outline-none focus:bg-[#FFF9C4] print:bg-transparent"
                      data-testid={`uitbetaald-input-${rij.medewerker_id}`}
                    />
                  </td>
                  )}
                  {canLonen && (
                  <td className="px-1 py-1 text-center font-semibold border border-[#999] tabular-nums bg-white w-[48px]" data-testid={`verschil-${rij.medewerker_id}`}>
                    {formatUren(rij.verschil_uren) || "0"}
                  </td>
                  )}
                  {canLonen && (
                  <td className="px-1 py-1 text-center border border-[#999] tabular-nums bg-white">
                    {rij.uurloon ? formatUren(rij.uurloon) : "0"}
                  </td>
                  )}
                  {canLonen && (
                  <td className="px-1 py-1 text-center font-bold border border-[#999] tabular-nums bg-white">
                    {formatLoon(rij.totaal_loon)}
                  </td>
                  )}
                  <td className="px-1.5 py-1 text-left border border-[#999] bg-white text-[10px] leading-tight" data-testid={`filialen-${rij.medewerker_id}`}>
                    {(rij.filiaal_detail || []).length === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      (rij.filiaal_detail || []).map((fd) => (
                        <span key={fd.filiaal_id} className="block whitespace-nowrap">
                          {fd.naam} <span className="font-bold tabular-nums">{formatUren(fd.uren)}u</span>
                        </span>
                      ))
                    )}
                  </td>
                </tr>
              ))}
              {/* Totals row */}
              <tr className="font-black">
                <td className="sticky left-0 z-10 bg-[#E0FFFF] px-2 py-1.5 font-black border border-[#999] print:static">
                  TOTAAL
                </td>
                {data.dagen.map((d, idx) => {
                  const dagTotaal = data.rijen.reduce((sum, r) => sum + (r.uren_per_dag[idx] || 0), 0);
                  let cellBg = "bg-[#E0FFFF]";
                  if (d.is_feestdag) cellBg = "bg-[#8E24AA] text-white";
                  else if (d.is_zondag) cellBg = "bg-[#FF6666]";
                  else if (d.is_zaterdag) cellBg = "bg-[#00CED1]";
                  return (
                    <td key={idx} className={`px-0.5 py-1.5 text-center tabular-nums border border-[#999] text-[9px] ${cellBg}`}>
                      {dagTotaal > 0 ? formatUren(round2(dagTotaal)) : ""}
                    </td>
                  );
                })}
                <td className="px-1 py-1.5 text-center font-black border border-[#999] tabular-nums bg-[#E0FFFF]">
                  {formatUren(data.totaal_uren)}
                </td>
                {canLonen && (
                <td className="px-1 py-1.5 text-center font-black border border-[#999] tabular-nums bg-[#E0FFFF]" data-testid="totaal-uitbetaald">
                  {formatUren(data.totaal_uitbetaald)}
                </td>
                )}
                {canLonen && (
                <td className="px-1 py-1.5 text-center font-black border border-[#999] tabular-nums bg-[#E0FFFF]" data-testid="totaal-verschil">
                  {formatUren(data.totaal_verschil)}
                </td>
                )}
                {canLonen && <td className="bg-[#E0FFFF] border border-[#999]"></td>}
                {canLonen && (
                <td className="px-1 py-1.5 text-center font-black border border-[#999] tabular-nums bg-[#E0FFFF]">
                  {formatLoon(data.totaal_loon)}
                </td>
                )}
                <td className="bg-[#E0FFFF] border border-[#999]"></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="hidden lg:flex items-center gap-3 mt-2 text-[10px] flex-wrap" data-testid="maand-legende">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 inline-block bg-[#8E24AA] border border-[#999]" /> Feestdag
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 inline-block bg-[#FF6666] border border-[#999]" /> Zondag
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 inline-block bg-[#00CED1] border border-[#999]" /> Zaterdag
          </span>
          <span className="text-muted-foreground">
            Extra uren voor zondag/feestdag (instelbaar per medewerker) zitten in de dagtotalen
          </span>
        </div>
        </>
      ) : null}

      {/* Etiketten Dialog */}
      {data && (
        <EtikettenDialog
          open={etikettenOpen}
          onOpenChange={setEtikettenOpen}
          rijen={data.rijen}
          jaar={jaar}
          maand={maand}
          maandNaam={MAAND_NAMEN[parseInt(maand) - 1]}
        />
      )}

      {/* Email Dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-md rounded-sm" data-testid="email-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Rapport Versturen</DialogTitle>
            <DialogDescription>Verstuur het maandoverzicht van {MAAND_NAMEN[parseInt(maand) - 1]} {jaar} per email met Excel bijlage.</DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-xs font-semibold">Email Adres</Label>
            <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="collega@gontex.be" className="rounded-sm mt-1" data-testid="email-input" />
            {recentEmails.length > 0 && (
              <div className="mt-3">
                <Label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Recent gebruikt</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="recent-emails">
                  {recentEmails.map((addr) => (
                    <div key={addr} className="group inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-border hover:border-[#002FA7] bg-muted/40 text-xs">
                      <button
                        type="button"
                        onClick={() => setEmailTo(addr)}
                        className="hover:text-[#002FA7]"
                        data-testid={`pick-email-${addr}`}
                      >
                        {addr}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeRecent(addr)}
                        className="text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity"
                        title="Verwijder uit lijst"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-3">
              Excel-bijlage wordt meegestuurd. Collega kan deze direct afdrukken op zijn lokale printer.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} className="rounded-sm">Annuleren</Button>
            <Button onClick={handleSendEmail} disabled={sending} className="rounded-sm bg-[#002FA7] hover:bg-[#002280] text-white" data-testid="send-email-btn">
              <EnvelopeSimple size={16} weight="bold" className="mr-2" />
              {sending ? "Versturen..." : "Versturen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function round2(val) {
  return Math.round(val * 100) / 100;
}
